<template>
  <div
    :id="`${source}_${type}_message`"
    :class="typeCSS[type].css"
    :style="customColorStyle"
  >
    <ThemeImage
      :icon="typeCSS[type].src"
      :alt="typeCSS[type].alt"
      class="size-5 mt-[2px]"
    />

    <div
      :class="[
        'flex w-full proxima_nova',
        slotDirection === customComponentDirections.VERTICAL
          ? 'flex-col'
          : 'flex-row justify-between',
      ]"
    >
      <div
        :class="[
          'flex flex-col text_color proxima_nova',
          title ? 'gap-1' : 'gap-[2px]',
        ]"
      >
        <span class="text-base font-semibold">{{ title }}</span>
        <span class="text-sm font-normal">{{ description }}</span>
      </div>

      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { customComponentDirections } from "@/src/constants/customComponentConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";

const props = defineProps({
  source: { type: String, required: true },
  type: { type: String, required: true },
  title: { type: String },
  description: { type: String },
  customColor: { type: String, required: false },
  slotDirection: { type: String, default: customComponentDirections.VERTICAL },
});

const typeCSS = {
  info: {
    css: "info_message",
    src: imageNames.INFO,
    alt: "info_message_icon",
    borderColor: "var(--white-opacity-60)",
    bgColor: "var(--white-opacity-8)",
  },
  warning: {
    css: "warning_message",
    src: imageNames.WARNING,
    alt: "warning_message_icon",
    borderColor: "rgba(170, 124, 35, 0.6)",
    bgColor: "rgba(170, 124, 35, 0.08)",
  },
  upgrade: {
    css: "upgrade_message",
    src: imageNames.LIGHTNING,
    alt: "upgrade_message_icon",
    borderColor: "var(--purple-tag-bg)",
    bgColor: "var(--upgrade-message-bg)",
  },
  announcement: {
    css: "announcement_message",
    src: imageNames.ANNOUNCEMENT,
    alt: "announcement_message_icon",
    borderColor: "rgba(24, 144, 255, 0.60)",
    bgColor: "rgba(24, 144, 255, 0.08)",
  },
};

const extractOpacity = (color) => {
  try {
    const match = color.match(/[\d.]+\)$/);
    return match ? parseFloat(match[0]) : 0.6;
  } catch (error) {
    console.error("An error occurred while extracting opacity: ", error);
  }
};

const customColorStyle = computed(() => {
  try {
    if (!props.customColor) {
      return {
        borderLeftColor: typeCSS[props.type].borderColor,
        backgroundColor: typeCSS[props.type].bgColor,
      };
    }

    const opacity = extractOpacity(props.customColor);
    const bgOpacity = opacity > 0.1 ? 0.08 : opacity;
    const bgColor = props.customColor.replace(/[\d.]+\)$/, `${bgOpacity})`);

    return {
      borderLeftColor: props.customColor,
      backgroundColor: bgColor,
    };
  } catch (error) {
    console.error(
      "An error occurred while handling custom color style: ",
      error,
    );
  }
});
</script>

<style scoped>
.info_message,
.warning_message,
.upgrade_message,
.announcement_message {
  display: flex;
  flex-direction: row;
  padding: 12px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  border-left: 4px solid;
}
</style>
